import { useCallback, useEffect, useRef, useState } from "react";
import ContainerCustom from "../../../Components/ContainerCustom";
import Table from "../../../Components/Table/Table";
import TableButton from "../../../Components/Buttons/TableButton";
import StatusColorful from "../../../Components/Table/Components/StatusColorful";
import ModalSimple from "../../../Components/ModalSimple";
import WithdrawModal from "./WithdrawModal";
import DepositModal from "./DepositModal";
import { isoToJalali } from "../../../helper/isoToJalali";
import { getTransactionHistoryTab } from "../transactionHistoryTabs";
import { API_BASE_URL } from "../../../config/api";

const REFRESH_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;
const TRANSACTION_FILTER_QUERY_MAP = ["completed", "pending", "rejected"];
const PAYMENT_METHOD_LABELS = {
  manual: "کیف کریپتو",
  directpay: "درگاه ریالی",
  nowpayment: "درگاه کریپتو",
};

function logoutAndRedirect() {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  window.location.href = "/signin";
}

async function authorizedGet(url) {
  let response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });

  if (response.status === 401) {
    const refreshResponse = await fetch(REFRESH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logoutAndRedirect();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);

    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

function normalizeStatus(status) {
  const value = String(status || "").toLowerCase();

  if (value === "failed") return "rejected";
  if (value === "done" || value === "approved") return "completed";

  return value;
}

function getSelectedBackendFilters(activeFilter) {
  return activeFilter.flatMap((isActive, index) =>
    isActive && TRANSACTION_FILTER_QUERY_MAP[index] !== undefined
      ? [TRANSACTION_FILTER_QUERY_MAP[index]]
      : [],
  );
}

function buildTransactionsUrl({ endpoint, page, activeFilter, searchTerm }) {
  const params = new URLSearchParams();
  const selectedFilters = getSelectedBackendFilters(activeFilter);

  params.set("page", String(page));
  params.set("filter", selectedFilters.join(","));

  if (searchTerm) {
    params.set("search", searchTerm);
  }

  return `${endpoint}?${params.toString()}`;
}

function mergeTransactions(previousItems, nextItems) {
  const seen = new Set(previousItems.map((item) => item.id ?? item.code));
  const uniqueItems = nextItems.filter((item) => {
    const key = item.id ?? item.code;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return [...previousItems, ...uniqueItems];
}

function getRelatedAccount(item) {
  return (
    item.related_account ?? item.account_code ?? item.challenge_name ?? "-"
  );
}

function hasDisplayValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function formatAmount(value) {
  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return numericValue.toLocaleString("en-US");
  }

  return String(value);
}

function getPaymentMethodLabel(paymentType) {
  const normalizedPaymentType = String(paymentType || "").toLowerCase();

  return PAYMENT_METHOD_LABELS[normalizedPaymentType] ?? "-";
}

function getTransactionType(item, tabConfig) {
  if (tabConfig.key === "payments") {
    return getPaymentMethodLabel(item.payment_type);
  }

  return tabConfig.typeLabel;
}

function getPaidAmount(item, normalizedStatus, tabConfig) {
  if (tabConfig.key === "payments") {
    if (hasDisplayValue(item.amount_irr)) {
      return `${formatAmount(item.amount_irr)} ریال`;
    }

    if (hasDisplayValue(item.amount)) {
      return `${formatAmount(item.amount)}$`;
    }

    return "-";
  }

  if (normalizedStatus === "pending") return "-";
  return item.amount ?? item.actual_amount_usd ?? "-";
}

function mapTransactionRow(item, tabConfig) {
  const normalizedStatus = normalizeStatus(item.status);

  return {
    codeRaw: item.code ?? item.id ?? "-",
    uuid: item.code ?? item.id ?? "-",
    type: getTransactionType(item, tabConfig),
    dateCreated: item.created_at ? isoToJalali(item.created_at) : "-",
    un: item.user_full_name ?? item.user_email ?? "-",
    account: getRelatedAccount(item),
    paid: getPaidAmount(item, normalizedStatus, tabConfig),
    paymentType: item.payment_type ?? "",
    dateDone:
      normalizedStatus !== "pending" && item.updated_at
        ? isoToJalali(item.updated_at)
        : "-",
    status: normalizedStatus,
    detail: "",
  };
}

function renderStatusCell(value) {
  if (value === "completed") {
    return (
      <StatusColorful
        SVGName="tick"
        text="تکمیل شده"
        className="w-24"
        color="green"
      />
    );
  }

  if (value === "pending") {
    return (
      <StatusColorful
        SVGName="stall"
        text="در انتظار"
        className="w-24"
        color="yellow"
      />
    );
  }

  if (value === "rejected") {
    return (
      <StatusColorful
        SVGName="failed"
        text="رد شده"
        className="w-24"
        color="red"
      />
    );
  }

  return "";
}

export default function TransactionsList({ tabKey }) {
  const tabConfig = getTransactionHistoryTab(tabKey);
  const typeColumnLabel =
    tabConfig.key === "payments" ? "روش پرداخت" : "نوع تراکنش";

  const [activeFilter, setActiveFilter] = useState([true, true, true]);
  const [searchState, setSearchState] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [showModalWithdraw, setshowModalWithdraw] = useState(false);
  const [showModalDeposit, setshowModalDeposit] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [depositCode, setDepositCode] = useState(null);
  const [depositPaymentType, setDepositPaymentType] = useState("");
  const [withdrawCode, setWithdrawCode] = useState(null);

  const [transactionData, setTransactionData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const requestIdRef = useRef(0);
  const loadMoreRef = useRef(null);

  const fetchTransactionData = useCallback(
    async ({ page = 1, replace = false, searchTerm }) => {
      const selectedFilters = getSelectedBackendFilters(activeFilter);

      if (selectedFilters.length === 0) {
        requestIdRef.current += 1;
        setTransactionData([]);
        setTableData([]);
        setPagination({
          page: 1,
          count: 0,
          pageSize: 0,
          totalPages: 0,
        });
        setIsLoading(false);
        setIsLoadingMore(false);
        setError("");
        return;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setError("");

      if (replace) {
        setIsLoading(true);
        setIsLoadingMore(false);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const data = await authorizedGet(
          buildTransactionsUrl({
            endpoint: tabConfig.endpoint,
            page,
            activeFilter,
            searchTerm,
          }),
        );

        if (!data || requestId !== requestIdRef.current) {
          return;
        }

        const nextTransactions = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data)
            ? data
            : [];

        setTransactionData((previousItems) =>
          replace
            ? nextTransactions
            : mergeTransactions(previousItems, nextTransactions),
        );
        setPagination({
          page: data.page ?? page,
          count: data.count ?? nextTransactions.length,
          pageSize: data.page_size ?? nextTransactions.length,
          totalPages: data.total_pages ?? page,
        });
      } catch (fetchError) {
        if (requestId !== requestIdRef.current) {
          return;
        }

        console.error("Error fetching transaction data:", fetchError);
        setError("خطا در دریافت لیست تراکنش‌ها");

        if (replace) {
          setTransactionData([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [activeFilter, tabConfig.endpoint],
  );

  useEffect(() => {
    fetchTransactionData({
      page: 1,
      replace: true,
      searchTerm: appliedSearch,
    });
  }, [appliedSearch, fetchTransactionData]);

  useEffect(() => {
    const selectedStatuses = getSelectedBackendFilters(activeFilter);

    const filteredRows = transactionData
      .map((item) => mapTransactionRow(item, tabConfig))
      .filter((item) => selectedStatuses.includes(item.status));

    setTableData(filteredRows);
  }, [activeFilter, tabConfig, transactionData]);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target) {
      return;
    }

    const scrollContainer = target.closest(".overflow-auto");
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (
          !entry?.isIntersecting ||
          isLoading ||
          isLoadingMore ||
          pagination.page >= pagination.totalPages ||
          pagination.totalPages === 0
        ) {
          return;
        }

        fetchTransactionData({
          page: pagination.page + 1,
          replace: false,
          searchTerm: appliedSearch,
        });
      },
      {
        root: scrollContainer,
        rootMargin: "200px 0px",
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [
    appliedSearch,
    fetchTransactionData,
    isLoading,
    isLoadingMore,
    pagination.page,
    pagination.totalPages,
  ]);

  const handleSearchClick = () => {
    const nextSearch = searchState.trim();

    if (nextSearch === appliedSearch) {
      fetchTransactionData({
        page: 1,
        replace: true,
        searchTerm: nextSearch,
      });
      return;
    }

    setAppliedSearch(nextSearch);
  };

  const handleOpenDetails = (row) => {
    if (tabConfig.modalType === "deposit") {
      setDepositCode(row.codeRaw);
      setDepositPaymentType(row.paymentType);
      setshowModalDeposit(true);
      return;
    }

    setWithdrawCode(row.codeRaw);
    setshowModalWithdraw(true);
  };

  return (
    <>
      <ContainerCustom
        header={tabConfig.header}
        headSVG="archive"
        colorfulFilterOptions={[
          {
            SVGName: "tick",
            text: "تکمیل شده",
            className: "w-24",
            color: "green",
          },
          {
            SVGName: "stall",
            text: "در انتظار",
            className: "w-24",
            color: "yellow",
          },
          {
            SVGName: "failed",
            text: "رد شده",
            className: "w-24",
            color: "red",
          },
        ]}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        filter={false}
        sort={false}
        search={true}
        searchValue={searchState}
        setSearchState={setSearchState}
        searchClick={handleSearchClick}
      >
        <div className="flex flex-col">
          <Table
            columns={[
              { key: "uuid", label: "کد تراکنش" },
              { key: "type", label: typeColumnLabel },
              { key: "dateCreated", label: "تاریخ ایجاد" },
              { key: "un", label: "نام کاربر" },
              { key: "account", label: "حساب مرتبط" },
              { key: "paid", label: "مبلغ نهایی پرداختی" },
              { key: "dateDone", label: "تاریخ تکمیل" },
              {
                key: "status",
                label: "وضعیت",
                render: (value) => renderStatusCell(value),
              },
              {
                key: "detail",
                label: "جزئیات",
                render: (value, row) => (
                  <TableButton
                    text="مشاهده"
                    href=""
                    onClick={() => handleOpenDetails(row)}
                    className="!py-1 !px-2 !rounded-md bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"
                  />
                ),
              },
            ]}
            data={tableData}
          />
          {error && (
            <div className="pt-4 text-center text-sm text-red-500">{error}</div>
          )}
          {!error && !isLoading && tableData.length === 0 && (
            <div className="pt-4 text-center text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
              نتیجه‌ای یافت نشد
            </div>
          )}
          <div
            ref={loadMoreRef}
            className="pt-4 text-center text-sm text-zi-text-light/50 dark:text-zi-text-dark/50"
          >
            {isLoading || isLoadingMore ? "در حال بارگذاری..." : ""}
          </div>
        </div>
      </ContainerCustom>
      {showModalWithdraw && tabConfig.modalType === "withdraw" && (
        <ModalSimple
          showModal={showModalWithdraw}
          setShowModal={setshowModalWithdraw}
        >
          <WithdrawModal
            onClose={() => setshowModalWithdraw(false)}
            withdrawCode={withdrawCode}
            modalConfig={tabConfig.withdrawModal}
          />
        </ModalSimple>
      )}
      {showModalDeposit && tabConfig.modalType === "deposit" && (
        <ModalSimple
          showModal={showModalDeposit}
          setShowModal={setshowModalDeposit}
        >
          <DepositModal
            onClose={() => setshowModalDeposit(false)}
            depositCode={depositCode}
            paymentType={depositPaymentType}
          />
        </ModalSimple>
      )}
    </>
  );
}
